import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';

interface ScanningAnimationProps {
  isActive: boolean;
  progress?: number;
}

// Generate random data points for the animation
function generateDataPoints(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 1 + Math.random() * 2,
  }));
}

export default function ScanningAnimation({
  isActive,
  progress = 0,
}: ScanningAnimationProps) {
  const [dataPoints] = useState(() => generateDataPoints(20));
  const [phase, setPhase] = useState(0);

  // Cycle through analysis phases
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setPhase(p => (p + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, [isActive]);

  const phases = [
    'Scanning price action...',
    'Identifying order blocks...',
    'Detecting liquidity zones...',
    'Calculating entry points...',
  ];

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 z-10 pointer-events-none overflow-hidden"
        >
          {/* Dark overlay - semi-transparent to show chart scan animation on top */}
          <div className="absolute inset-0 bg-bg-primary/30" />

          {/* Neon grid background */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(0, 255, 136, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 255, 136, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
            }}
          />

          {/* Subtle particle effects around edges - reduced since ChartScanAnimation handles main scan */}
          {dataPoints.slice(0, 8).map((point) => (
            <motion.div
              key={point.id}
              className="absolute w-1 h-1 rounded-full"
              style={{
                left: `${point.x}%`,
                top: `${point.y}%`,
                backgroundColor: point.id % 2 === 0 ? '#00ff88' : '#00ffff',
                boxShadow: `0 0 4px ${point.id % 2 === 0 ? '#00ff88' : '#00ffff'}`,
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1, 1, 0],
                opacity: [0, 0.5, 0.5, 0],
              }}
              transition={{
                duration: point.duration,
                delay: point.delay,
                repeat: Infinity,
                repeatDelay: 1.5,
              }}
            />
          ))}

          {/* Corner brackets are now rendered in ChartScanAnimation for precise positioning */}

          {/* Center content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
            {/* Pulsing rings */}
            <div className="relative">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={`ring-${i}`}
                  className="absolute inset-0 rounded-full border border-[#00ff88]"
                  style={{
                    width: 80 + i * 20,
                    height: 80 + i * 20,
                    left: -(i * 10),
                    top: -(i * 10),
                  }}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.3,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}

              {/* Center icon */}
              <motion.div
                className="w-20 h-20 rounded-full flex items-center justify-center relative"
                style={{
                  background: 'radial-gradient(circle, rgba(0, 255, 136, 0.2) 0%, transparent 70%)',
                  border: '2px solid #00ff88',
                  boxShadow: '0 0 20px #00ff88, inset 0 0 20px rgba(0, 255, 136, 0.3)',
                }}
                animate={{
                  boxShadow: [
                    '0 0 20px #00ff88, inset 0 0 20px rgba(0, 255, 136, 0.3)',
                    '0 0 40px #00ff88, inset 0 0 30px rgba(0, 255, 136, 0.5)',
                    '0 0 20px #00ff88, inset 0 0 20px rgba(0, 255, 136, 0.3)',
                  ],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                {/* AI brain icon */}
                <motion.svg
                  className="w-10 h-10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#00ff88"
                  strokeWidth="1.5"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                >
                  <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4Z" />
                  <path d="M8 7a4 4 0 0 0-4 4v1a4 4 0 0 0 8 0v-1a4 4 0 0 0-4-4Z" />
                  <path d="M16 7a4 4 0 0 1 4 4v1a4 4 0 0 1-8 0v-1a4 4 0 0 1 4-4Z" />
                  <path d="M8 17a4 4 0 0 0 4 4 4 4 0 0 0 4-4" />
                  <circle cx="12" cy="12" r="2" fill="#00ff88" />
                </motion.svg>
              </motion.div>
            </div>

            {/* Text with neon glow */}
            <div className="text-center">
              <motion.p
                className="text-xl font-bold tracking-wider"
                style={{
                  color: '#00ff88',
                  textShadow: '0 0 10px #00ff88, 0 0 20px #00ff88',
                }}
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                AI ANALYZING
              </motion.p>
              <motion.p
                key={phase}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-sm text-[#00ffff] mt-2 font-mono"
                style={{ textShadow: '0 0 5px #00ffff' }}
              >
                {phases[phase]}
              </motion.p>
            </div>

            {/* Progress bar with neon effect */}
            <div className="w-56 relative">
              <div className="h-1 bg-bg-tertiary/50 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #00ff88, #00ffff)',
                    boxShadow: '0 0 10px #00ff88',
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: progress > 0 ? `${progress}%` : ['0%', '30%', '60%', '30%', '0%'] }}
                  transition={progress > 0 ? { duration: 0.3 } : { duration: 3, repeat: Infinity }}
                />
              </div>
              {progress > 0 && (
                <motion.span
                  className="absolute -right-8 top-1/2 -translate-y-1/2 text-xs font-mono text-[#00ff88]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {progress}%
                </motion.span>
              )}
            </div>
          </div>

          {/* Decorative floating elements */}
          <motion.div
            className="absolute top-1/4 left-1/4 text-xs font-mono text-[#00ff88]/60"
            animate={{ opacity: [0, 1, 0], y: [0, -20, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            OB+
          </motion.div>
          <motion.div
            className="absolute top-1/3 right-1/4 text-xs font-mono text-[#ff4466]/60"
            animate={{ opacity: [0, 1, 0], y: [0, -20, 0] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
          >
            FVG
          </motion.div>
          <motion.div
            className="absolute bottom-1/3 left-1/3 text-xs font-mono text-[#00ffff]/60"
            animate={{ opacity: [0, 1, 0], y: [0, -20, 0] }}
            transition={{ duration: 3, repeat: Infinity, delay: 2 }}
          >
            BSL
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
